<script setup>
import { ref, watch, computed, toRef, nextTick } from 'vue'
import RemoveResourceButton from '@/library/RemoveResourceButton.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import ResourcePreviewModal from '@/resources/ResourcePreviewModal.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import { getResourceSubjectLabelsFromValues } from '@/contentprofile/no_sel_composable'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useUserStore } from '@/authentication/user.store'
import GbButton from '@/components/GbButton.vue'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import CardContributor from '@/library/CardContributor.vue'
import { useContributorVisibility } from '@/library/composables/useContributorVisibility'
import DynamicHeading from '@/components/DynamicHeading.vue'
import { announce } from '@/util/live_announcer'
import ResourceAlignedGoal from '@/resources/ResourceAlignedGoal.vue'
import { toolkitUrls } from '@/util/links.js'
import { withoutProgressMonitoringPrefix } from '@/resources/resource_tile_titles'

const props = defineProps({
  uuid: {
    required: true,
    type: String
  },
  title: {
    required: true,
    type: String
  },
  description: {
    required: true,
    type: String
  },
  type: {
    required: true,
    type: String
  },
  thumbnail_url: {
    required: true,
    type: String
  },
  grades: {
    required: true,
    type: Array
  },
  subjects: {
    required: true,
    type: Array
  },
  num_pages: {
    default: () => {
      return 0
    },
    type: Number
  },
  screenshots: {
    required: false,
    type: Array,
    default: () => {
      return []
    }
  },
  download_links: {
    required: true,
    type: Array
  },
  google_drive_links: {
    required: false,
    type: Array,
    default: () => {
      return []
    }
  },
  showThumbnail: {
    type: Boolean,
    default: true
  },
  language: {
    type: String,
    default: null
  },
  showResourceType: {
    type: Boolean,
    default: true
  },
  omitDescription: {
    type: Boolean,
    default: false
  },
  collectionUuid: {
    type: String,
    default: null
  },
  removable: {
    type: Boolean,
    default: true
  },
  shared: {
    type: Boolean,
    default: false
  },
  set_number: {
    type: Number,
    default: 0
  },
  headingLevel: {
    type: Number,
    default: 3
  },
  // Shown in place of the type, such as "Baseline Assessment" on Browse Assessments.
  typeHeader: {
    type: String,
    default: ''
  },
  rationale: {
    type: String,
    default: null
  },
  relevanceScore: {
    type: Number,
    default: null
  },
  alignedGoal: {
    type: Object,
    default: null
  },
  showUnauthenticated: {
    type: Boolean,
    default: false
  },
  eventContext: {
    type: Object,
    default: () => ({})
  }
})
const showPreviewModal = ref(false)
const showSaveModal = ref(false)
const resourceType = ref(CONTENT_TYPES.RESOURCE)
const analytics = getAnalyticsTracker().getChildContext({
  resource_preview_type: 'card',
  resource_uuid: toRef(props, 'uuid'),
  resource_title: toRef(props, 'title'),
  resource_type: toRef(props, 'type'),
  resource_grades: toRef(props, 'grades'),
  resource_subjects: toRef(props, 'subjects'),
  resource_language: toRef(props, 'language'),
  ...props.eventContext
})

const displaySubjects = ref(getResourceSubjectLabelsFromValues(props.subjects).filter(s => s !== ''))

const emit = defineEmits([
  'resourcePreviewShown',
  'resourceDownloadClicked',
  'resourceDownloadOpened',
  'resourceSaved'
])
// The preview unmounts with the card's v-if, so PrimeVue never runs the leave transition that would refocus its opener.
const previewLink = ref(null)
const togglePreviewModal = (visible) => {
  showPreviewModal.value = visible
  if (!visible) {
    nextTick(() => previewLink.value?.focus())
    return
  }
  analytics.sendEvent(EVENT_NAMES.RESOURCES.SHOWED_PREVIEW, { resource_preview_type: 'modal' }, { cu: props.uuid, ct: props.title })
  emit('resourcePreviewShown')
}
const toggleSaveModal = (visible) => {
  showSaveModal.value = visible
  if (visible) {
    analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_SAVE)
    emit('resourceSaved')
  }
}

// The modal emits save while it is still open, and closing it cancels any announcement
// pending in its own live regions, so wait for it to close before announcing.
const savedFolder = ref(null)
const confirmSave = async (folder) => {
  await nextTick()
  announce(`Resource was saved to ${folder.name}`, { assertive: true })
  savedFolder.value = folder
}

const { selVisibility } = storeToRefs(useVisibilityStore())

watch(selVisibility, () => {
  displaySubjects.value = getResourceSubjectLabelsFromValues(props.subjects).filter(s => s !== '')
})

const { authInfo } = storeToRefs(useUserStore())

const savePermissions = computed(() => {
  return actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.RESOURCE, {}, authInfo.value)
})
const { contributorIsVisible } = useContributorVisibility(props.uuid)

const titleWithSet = computed(() => {
  const title = withoutProgressMonitoringPrefix(props.title)
  if (props.set_number && props.set_number !== 0) {
    return `${title} - Set ${props.set_number}`
  }
  return title
})

const resourceDetails = computed(() => {
  const gradeLabel = props.grades.length > 0 ? `Grade ${props.grades.join(', ')}` : ''
  const subjectLabel = displaySubjects.value.join(', ')
  const pageCountLabel = props.num_pages > 0 ? `${props.num_pages} pages` : ''
  return [gradeLabel, subjectLabel, pageCountLabel].filter(Boolean).join(' · ')
})

const isTypeLabelShown = computed(() => props.showResourceType || Boolean(props.typeHeader))
const typeLabel = computed(() => props.typeHeader || props.type)
const hasRationale = computed(() => Boolean(props.rationale?.trim()))
const hasRelevanceScore = computed(() => props.relevanceScore !== null)

const resolvedHeadingLevel = computed(() => {
  if (Number.isInteger(props.headingLevel) && props.headingLevel >= 1 && props.headingLevel <= 6) {
    return props.headingLevel
  }
  return 3
})
</script>
<template>
  <div class="flex flex-col px-5 pt-5 sm:flex-row">
    <div v-if="showThumbnail && screenshots.length > 0" class="flex flex-col items-center p-5 text-center sm:items-stretch">
      <!-- The thumbnail repeats the Preview button for the mouse, so it stays out of the tab order and the accessibility tree, as on Vue 2 -->
      <div
        aria-hidden="true"
        @click="togglePreviewModal(true)"
        class="grid hover:cursor-pointer [&_.invisible]:hover:!visible [&_.invisible]:hover:opacity-70"
        :title="`Preview ${title}`"
      >
        <div
          class="invisible z-30 col-span-full row-span-full flex h-full flex-col bg-black uppercase text-white"
        >
          <span class="material-icons-outlined pt-[50%]"> zoom_in </span>
          <span class="grow">Preview</span>
        </div>
        <div class="z-10 col-span-full row-span-full rotate-[-5deg] border border-gray-600" />
        <IKImageS3Wrapper
          :aws-url="thumbnail_url"
          alt=""
          :transformation="[{ width: 150 }]"
          class="z-20 col-span-full row-span-full border border-gray-600"
        />
      </div>
      <button
        ref="previewLink"
        type="button"
        @click="togglePreviewModal(true)"
        :aria-label="`Preview ${title}`"
        class="pt-2 text-md text-blue-400"
      >
        Preview
      </button>
    </div>

    <div class="flex min-w-0 flex-col">
      <div class="leading-4">
        <span
          class="text-sm font-medium uppercase leading-5 text-gray-700"
          v-if="isTypeLabelShown"
          >{{ typeLabel }}</span
        >
        <span
          class="ml-2 rounded-xl bg-gray-800 px-2 py-0.5 text-sm text-white"
          v-if="language && language !== 'english'"
          >{{ $filters.capitalize(language) }}</span
        >
      </div>
      <DynamicHeading
        :headingLevel="resolvedHeadingLevel"
        :class="`${contributorIsVisible ? 'pb-1' : 'pb-2'} text-xl font-bold`"
        data-cy="resource-title"
      >
        {{ titleWithSet }}
      </DynamicHeading>
      <CardContributor v-if="!shared" :contentId="uuid"/>
      <p class="text-md" v-if="!omitDescription">{{ description }}</p>
      <div v-if="hasRationale" class="ai-border mb-2 mt-4 flex flex-col gap-1.5 !rounded-xs p-4 text-md leading-5">
        <p class="font-bold italic">Why this assessment?</p>
        <p class="italic">{{ rationale }}</p>
        <p v-if="hasRelevanceScore" class="font-bold text-gray-700">Relevance {{ relevanceScore }}/10</p>
      </div>
      <ResourceAlignedGoal v-if="alignedGoal" :goal="alignedGoal" />
      <slot name="resource-details">
        <div class="pt-2 text-md text-gray-700">{{ resourceDetails }}</div>
      </slot>
      <div class="flex flex-wrap gap-2 pt-3">
        <ResourceDownloadButton
          :resource="{
            uuid,
            title,
            type,
            language,
            download_links,
            google_drive_links,
            set_number
          }"
          :show-unauthenticated="showUnauthenticated"
          @resourceDownloadClicked="emit('resourceDownloadClicked')"
          @resourceDownloadOpened="emit('resourceDownloadOpened')"
        />
        <GbButton
          gbTheme="greenPrimary"
          v-if="!collectionUuid && !savePermissions.block"
          size="small"
          :aria-label="`Save ${title}`"
          @click="toggleSaveModal(true)"
        >
          <span class="material-icons-round pr-1 text-lg">save</span> Save
        </GbButton>
      </div>
      <!-- Not a live region: confirmSave already announces the save -->
      <div
        v-if="savedFolder"
        data-testid="resource-saved-confirmation"
        class="mt-3 flex items-start gap-2 rounded-xs bg-green-100 px-3 py-2 text-md text-gray-900"
      >
        <p class="flex-1">
          Resource was saved to <a :href="toolkitUrls.libraryFolder(savedFolder.uuid)" class="fs-exclude text-blue-400 hover:underline">{{ savedFolder.name }}</a>
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          class="material-icons-round text-text-xl leading-none text-gray-700 hover:text-gray-900"
          @click="savedFolder = null"
        >close</button>
      </div>
      <ResourcePreviewModal
        v-if="showPreviewModal"
        @update:visible="togglePreviewModal"
        :uuid="uuid"
        :title="title"
        :type="type"
        :download-links="download_links"
        :google-drive-links="google_drive_links"
        :screenshots="screenshots"
        :num-pages="num_pages"
        :language="language"
      />
    </div>
    <RemoveResourceButton
      class="!relative !right-[unset] !top-[unset] min-w-8 !opacity-100"
      v-if="collectionUuid && removable"
      :resource="uuid"
      :collection="collectionUuid"
      :type="resourceType"
      :ariaLabel="`Remove ${title} from collection`"
    />
    <SaveToFolderModal
      v-if="!collectionUuid && showSaveModal"
      @update:visible="toggleSaveModal"
      @save="confirmSave"
      :contentUuid="uuid"
      :content-title="title"
      content-type="resource"
      content-type-label="Resource"
    />
  </div>
</template>
